import localStatisticsActions from './localStatisticsActions';
import { getInsiktReporter } from '../../../analytics';
import { IpexMomentEnum } from '@insights/insights-data-provider';
import { isNonInteractionEvent, sanitizeObject } from '../../../utils/object';

interface CustomLocalEvent {
  ipexMoment: IpexMomentEnum;
  event: string;
  payload?: any;
  reportEvent: Function;
}

interface CustomLocalEvents {
  [key: string]: CustomLocalEvent;
}
/**
 * Builds the custom event by formatting it according to insights specifications.
 * Insights specification for events:
 * ipexMoment: 1/5 enum choices.
 * event: snake_case.
 * payload: single-depth object hierarchy with only primitives.
 * @param actionType
 * @param payload
 * @param meta
 * @returns {object}
 */
export const buildCustomLocalStatisticsEvent = (
  actionType: string,
  payload: any,
  meta: any
) => {
  const sanitizedPayload = sanitizeObject(payload);
  const sanitizedMeta = sanitizeObject(meta);

  const customLocalEvent = customLocalEvents[actionType];

  const { reportEvent, ...event } = {
    ...customLocalEvent,
    ipexMoment: isNonInteractionEvent(meta)
      ? IpexMomentEnum.nonInteraction
      : customLocalEvent.ipexMoment,
    payload: {
      ...sanitizedPayload,
      ...sanitizedMeta,
    },
  };
  return event;
};

/**
 * Used by other files/modules to access the customLocalEvents object.
 * @param localEventType
 * @returns ICustomLocalEvent
 */
export const getLocalEvent = (localEventType: string) => {
  return customLocalEvents[localEventType];
};

/**
 * Calls the insightsApi to report the custom event.
 * @param event
 * @param payload
 * @param meta
 */
const reportCustomLocalEvent = (event: string, payload: any, meta: any) => {
  const eventToReport = buildCustomLocalStatisticsEvent(event, payload, meta);
  getInsiktReporter().reportCustomEvent(eventToReport);
};

/**
 * Contains all custom local events that can be reported at any place in our codebase.
 * Needs to contain:
 * @property ipexMoment The type of event according to ikea specifications, determined by ourselves.
 * @property event The name of the event, always in snake case.
 * @property reportEvent All events have access to a reportEvent function.
 */
export const customLocalEvents: CustomLocalEvents = {
  [localStatisticsActions.CONF_MENU_INFO_BUTTON_CLICKED]: {
    ipexMoment: IpexMomentEnum.helpMe,
    event: localStatisticsActions.CONF_MENU_INFO_BUTTON_CLICKED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.CONF_MENU_SLIDER]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.CONF_MENU_SLIDER,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.TAC_ARTICLE_CLICKED]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.TAC_ARTICLE_CLICKED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.PRODUCT_MENU_MOUNTING_RAIL_INFO_CLICKED]: {
    ipexMoment: IpexMomentEnum.helpMe,
    event: localStatisticsActions.PRODUCT_MENU_MOUNTING_RAIL_INFO_CLICKED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.PRODUCT_MENU_SWIPER_INTERACTION]: {
    ipexMoment: IpexMomentEnum.whatIsMe,
    event: localStatisticsActions.PRODUCT_MENU_SWIPER_INTERACTION,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.VALIDATION_ERROR]: {
    ipexMoment: IpexMomentEnum.helpMe,
    event: localStatisticsActions.VALIDATION_ERROR,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.OPEN_PLANNER]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.OPEN_PLANNER,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.START_VIEW_SPR_CLICKED]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.START_VIEW_SPR_CLICKED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.ADD_UNAVAILABLE_TO_CART_AND_LIST]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.ADD_UNAVAILABLE_TO_CART_AND_LIST,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.ADD_UNAVAILABLE_TO_CART]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.ADD_UNAVAILABLE_TO_CART,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.ADD_TO_BAG_ERROR]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.ADD_TO_BAG_ERROR,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.ADD_TO_LIST_ERROR]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.ADD_TO_LIST_ERROR,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.START_VIEW_VPC_ENTER_CODE_SUCCESS]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.START_VIEW_VPC_ENTER_CODE_SUCCESS,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.OPEN_PLANNER_SPR_DEEPLINK_NOT_SUPPORTED]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.OPEN_PLANNER_SPR_DEEPLINK_NOT_SUPPORTED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.OPEN_PLANNER_VPC_DEEPLINK_NOT_SUPPORTED]: {
    ipexMoment: IpexMomentEnum.giveMe,
    event: localStatisticsActions.OPEN_PLANNER_VPC_DEEPLINK_NOT_SUPPORTED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.SURVEY_FEEDBACK]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.SURVEY_FEEDBACK,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.SHOW_SURVEY]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.SHOW_SURVEY,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.TERMINATE_SURVEY]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.TERMINATE_SURVEY,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.NOT_LOADED]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.NOT_LOADED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.SUB_PLANNER_USER_CHOICE]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.SUB_PLANNER_USER_CHOICE,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.SUB_PLANNER_AUTOMATIC_CHOICE]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.SUB_PLANNER_AUTOMATIC_CHOICE,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.LANGUAGE_SELECTOR_USER_CHOICE]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.LANGUAGE_SELECTOR_USER_CHOICE,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.PRODUCT_MENU_SUB_FILTER_INTERACTION]: {
    ipexMoment: IpexMomentEnum.showMe,
    event: localStatisticsActions.PRODUCT_MENU_SUB_FILTER_INTERACTION,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.SERIES_GALLERY_USAGE]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.SERIES_GALLERY_USAGE,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_DIALOG_SHOWN]: {
    ipexMoment: IpexMomentEnum.helpMe,
    event: localStatisticsActions.AMELIORATION_DIALOG_SHOWN,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_DIALOG_ACCEPTED]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.AMELIORATION_DIALOG_ACCEPTED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_DIALOG_DECLINED]: {
    ipexMoment: IpexMomentEnum.makeItMe,
    event: localStatisticsActions.AMELIORATION_DIALOG_DECLINED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_ATTEMPT]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.AMELIORATION_ATTEMPT,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_CAN_NOT_BE_SOLVED]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.AMELIORATION_CAN_NOT_BE_SOLVED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_SOLVED]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.AMELIORATION_SOLVED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
  [localStatisticsActions.AMELIORATION_DECLINED]: {
    ipexMoment: IpexMomentEnum.nonInteraction,
    event: localStatisticsActions.AMELIORATION_DECLINED,
    reportEvent: function (payload: any, meta: any) {
      reportCustomLocalEvent(this.event, payload, meta);
    },
  },
};
